import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger
} from '@radix-ui/react-accordion';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { dispatchGAEvent } from 'utils/googleAnalytics';
import { hasValue } from 'utils/hasValue';
import { updateHistoryState, updateURLSearchParams } from 'utils/url';

import type { CreatedPayoutDraft, CreatedPayoutDraftData } from 'api';
import {
  usePostDisbursementPayoutDrafts,
  usePutDisbursementPayoutDraftsDraftId,
  usePostDisbursementPayoutDraftsIdPublish
} from 'api';
import { Restricted } from 'containers/Restricted';

import { selectUserId } from 'reduxLocal/slices/authSlice';

import { useOrganisationId } from 'hooks/context';
import type { ConditionalStep } from 'hooks/useConditionalSteps';
import { useConditionalSteps } from 'hooks/useConditionalSteps';
import { useFeatureAccessControl } from 'hooks/useFeatureAccessControl';

import {
  CREATE_MANUAL_PAYOUT_DIALOG_TEST_ID,
  WEBSITE_STEP_NEXT_BUTTON_TEST_ID,
  SUMMARY_STEP_BUTTON_TEST_ID
} from './constants';
import { StepItem } from './StepItem';
import type { Steps } from './steps';
import { AcceptanceDialog } from './steps/AcceptanceDialog';
import { CommonStep } from './steps/CommonStep';
import { PayoutRequestStep } from './steps/PayoutRequestStep';
import { ReceiverStep } from './steps/ReceiverStep';
import { SelectProductStep } from './steps/SelectProductStep';
import { SenderStep } from './steps/SenderStep';
import { SummaryStep } from './steps/SummaryStep';
import { StepStatusCircle } from './StepStatusCircle';

type ApprovalData = {
  amount: string;
  currency: string;
  reference: string;
};

type Props = {
  onFinish: () => void;
  onCancel: () => void;
  onDraftCreated?: (id: number) => void;
  onShowApprovalDialog?: (params: ApprovalData) => void;
};

export const Content = ({
  onFinish,
  onCancel,
  onDraftCreated,
  onShowApprovalDialog
}: Props) => {
  const userId = useSelector(selectUserId);
  const [draftData, setDraftData] = useState<CreatedPayoutDraft>();

  const { isReceiverManagementEnabled, isManualPayoutApprovalFlowEnabled } =
    useFeatureAccessControl();
  const openNotification = useOpenNotification();

  const [fieldsDefinition, setFieldsDefinition] =
    useState<CreatedPayoutDraftData>();

  const steps: ConditionalStep<Steps>[] = useMemo(() => {
    return [
      { name: 'selectProduct' },
      { name: 'payoutRequest' },
      {
        name: 'debtor',
        visible: Boolean(
          Object.keys(fieldsDefinition?.['debtor']['website'] ?? {}).length
        )
      },
      { name: 'sender' },
      { name: 'receiver' },
      { name: 'receiverBank' },
      {
        name: 'intermediaryAgent',
        visible: Boolean(
          Object.keys(fieldsDefinition?.['intermediary_agent'] ?? {}).length
        )
      },
      {
        name: 'intermediaryAgentAccount',
        visible: Boolean(
          Object.keys(fieldsDefinition?.['intermediary_agent_account'] ?? {})
            .length
        )
      },
      { name: 'summary' }
    ];
  }, [fieldsDefinition]);

  const { currentStep, completeStep, setStep, isVisible, getStep, isFinished } =
    useConditionalSteps<Steps>(steps);

  const [stepsData, setStepsData] = useState<Partial<Record<Steps, any>>>({});

  const { mutateAsync: createDraft } = usePostDisbursementPayoutDrafts({
    mutation: {
      onSuccess: (data) => {
        setDraftData(data);
      },
      onError: (_) => {
        openNotification({ kind: 'error', message: 'Error creating draft' });
      }
    }
  });

  const { mutateAsync: updateDraft } = usePutDisbursementPayoutDraftsDraftId();

  const { mutateAsync: publishDraft } =
    usePostDisbursementPayoutDraftsIdPublish();
  const organisationId = useOrganisationId<number>();

  useEffect(() => {
    const url = updateURLSearchParams('createManualPayout', currentStep);
    updateHistoryState(url);
  }, [currentStep]);

  const [draftId, setDraftId] = useState<number | null>(null);
  const [approval, setApproval] = useState<ApprovalData | null>(null);

  const onSelectProductSubmit = async (data: any) => {
    if (!hasValue(userId)) return;

    let response: CreatedPayoutDraft | undefined;

    if (draftData) {
      response = draftData;
    } else {
      response = await new Promise((resolve, reject) => {
        createDraft(
          {
            organisationId,
            data: {
              schema_id: data.productSchemaId,
              product_id: data.corridorId
            }
          },
          {
            onSuccess: (data) => resolve(data as CreatedPayoutDraft),
            onError: (error) => reject(error)
          }
        );
      });
    }

    if (response?.data) {
      setDraftId(response.data.draft_id);
      onDraftCreated?.(response.data.draft_id);

      setFieldsDefinition(response.data);
      setStepsData((state) => ({
        ...state,
        selectProduct: {
          productId: data.corridorId,
          schemaId: data.productSchemaId
        }
      }));
      completeStep();
    }
  };

  const onStepSubmit = async (data: any, responseField: string) => {
    // Cleaned data removes empty strings like e2e_test_id = "" from the parameters
    const cleanedData = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value !== '')
    );
    if (hasValue(draftId)) {
      await updateDraft({
        organisationId,
        draftId,
        data: {
          [responseField]: cleanedData
        }
      })
        .then(() => {
          setStepsData((state) => ({
            ...state,
            [currentStep]: cleanedData
          }));

          completeStep();
        })
        .catch((e) => {
          openNotification({
            kind: 'warning',
            message: 'Error occurred while updating Payout draft'
          });
          throw e;
        });
    }
  };

  const onSenderSubmit = async (data: any) => {
    if (hasValue(draftId)) {
      await updateDraft({
        organisationId,
        draftId,
        data: {
          ultimate_debtor: data
        }
      });
    }

    const updatedData = { sender: data } as any;

    if (data.sameReceiver && isFinished('receiver') === false) {
      const senderFields = Object.keys(
        fieldsDefinition?.['ultimate_debtor'] || {}
      );
      const receiverFields = Object.keys(
        fieldsDefinition?.['ultimate_debtor'] || {}
      );
      const intersection = senderFields.filter((field) =>
        receiverFields.includes(field)
      );
      const receiverData = Object.fromEntries(
        intersection.map((field) => [field, data[field]])
      );

      updatedData.receiver = receiverData;
    }

    setStepsData((state) => ({
      ...state,
      ...updatedData
    }));

    completeStep();
  };

  const [uniqueReference, setUniqueReference] = useState<string | null>(null);
  const [draftWaitingReview, setDraftWaitingReview] = useState<boolean>(false);

  const onSummarySubmit = async () => {
    if (!hasValue(draftId)) return;

    const response = await publishDraft({
      organisationId,
      id: draftId
    });
    if (response?.data?.unique_reference) {
      const reference = response?.data?.unique_reference;
      setUniqueReference(reference);
    }
    if (
      response?.data?.state === 'waiting_review' &&
      hasValue(response?.data?.amount) &&
      hasValue(response?.data?.currency_code) &&
      hasValue(response?.data?.end_to_end_id)
    ) {
      setDraftWaitingReview(true);
      setApproval({
        amount: response.data.amount,
        currency: response.data.currency_code,
        reference: response.data.end_to_end_id
      });
    }
  };

  const onStepChange = (step: Steps) => {
    if (fieldsDefinition) {
      setStep(step);
      dispatchGAEvent('create_manual_payout_step_changed', {
        from: currentStep,
        to: step,
        version: 'v1'
      });
    }
  };

  useEffect(() => {
    if (
      isManualPayoutApprovalFlowEnabled &&
      approval !== null &&
      draftWaitingReview && // Isn't strictly needed here, since approval !== null iff draftWaitingReview equals true
      onShowApprovalDialog
    ) {
      onShowApprovalDialog({
        amount: approval.amount,
        currency: approval.currency,
        reference: approval.reference
      });
    }
  }, [
    approval,
    approval?.amount,
    approval?.currency,
    approval?.reference,
    draftWaitingReview,
    isManualPayoutApprovalFlowEnabled,
    onShowApprovalDialog
  ]);

  if (!userId) return null;

  return (
    <div
      className="overflow-auto rounded-b-lg"
      data-testid={CREATE_MANUAL_PAYOUT_DIALOG_TEST_ID}
    >
      <Accordion
        type="single"
        className="flex flex-col"
        value={currentStep}
        onValueChange={onStepChange}
      >
        <AccordionItem className="mt-2 rounded-2xl " value="selectProduct">
          <AccordionHeader>
            <AccordionTrigger className="group flex w-full cursor-pointer items-center justify-start gap-2 px-6 py-4 outline-offset-[-1px] focus:!outline-none focus:ring-0 group-last:radix-state-closed:rounded-b-lg">
              <StepStatusCircle
                step="selectProduct"
                value={getStep('selectProduct').index}
                isFinished={isFinished('selectProduct')}
              />
              <h2 className="font-semibold">Select Product</h2>
              <ChevronDownIcon className="group-radix-state-open:scale-flip ml-auto w-7 transition-all duration-300 group-hover:!stroke-content-primary group-radix-state-closed:stroke-content-muted" />
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent forceMount className="radix-state-closed:hidden">
            <SelectProductStep
              onSubmit={onSelectProductSubmit}
              onCancel={onCancel}
              fields={fieldsDefinition?.['product']}
              isFinished={!!fieldsDefinition}
              responseField="product"
            />
          </AccordionContent>
        </AccordionItem>

        <StepItem
          key="payoutRequest"
          {...getStep('payoutRequest')}
          header="Payout Request"
        >
          <PayoutRequestStep
            onSubmit={(data: any) => onStepSubmit(data, 'payout_request')}
            onCancel={onCancel}
            fields={fieldsDefinition?.['payout_request']}
            responseField="payout_request"
            defaultValues={stepsData.payoutRequest}
          />
        </StepItem>

        {isVisible('debtor') && (
          <StepItem key="debtor" {...getStep('debtor')} header="Website">
            <CommonStep
              onSubmit={(data: any) => onStepSubmit(data, 'debtor')}
              onCancel={onCancel}
              fields={fieldsDefinition?.['debtor']}
              responseField="debtor"
              defaultValues={stepsData.debtor}
              dataTestIdStepNextButton={WEBSITE_STEP_NEXT_BUTTON_TEST_ID}
            />
          </StepItem>
        )}

        <StepItem key="sender" {...getStep('sender')} header="Sender">
          <SenderStep
            onSubmit={onSenderSubmit}
            onCancel={onCancel}
            fields={fieldsDefinition?.['ultimate_debtor']}
            responseField="ultimate_debtor"
            defaultValues={stepsData.sender}
            sameReceiverInactive={isFinished('receiver')}
            setStepsData={setStepsData}
          />
        </StepItem>

        <StepItem key="receiver" {...getStep('receiver')} header="Receiver">
          {/* Checking for feature flag here, otherwise render old stuff */}
          {isReceiverManagementEnabled ? (
            <Restricted requiredPermission="receivers:read">
              <ReceiverStep
                onSubmit={(data: any) => onStepSubmit(data, 'creditor')}
                onCancel={onCancel}
                fields={fieldsDefinition?.['creditor']}
                responseField="creditor"
                defaultValues={stepsData.receiver}
                stepsData={stepsData}
                setStepsData={setStepsData}
              />
            </Restricted>
          ) : (
            <CommonStep
              onSubmit={(data: any) => onStepSubmit(data, 'creditor')}
              onCancel={onCancel}
              fields={fieldsDefinition?.['creditor']}
              responseField="creditor"
              defaultValues={stepsData.receiver}
              fieldToDisplayableNameSchema="RECEIVER_FIELD_NAMES"
            />
          )}
        </StepItem>

        <StepItem
          key="receiverBank"
          {...getStep('receiverBank')}
          header="Receiver Bank"
        >
          <CommonStep
            onSubmit={(data: any) => onStepSubmit(data, 'creditor_account')}
            onCancel={onCancel}
            fields={fieldsDefinition?.['creditor_account']}
            responseField="creditor_account"
            defaultValues={stepsData.receiverBank}
            fieldToDisplayableNameSchema="RECEIVER_FIELD_NAMES"
          />
        </StepItem>

        {isVisible('intermediaryAgent') && (
          <StepItem
            key="intermediaryAgent"
            {...getStep('intermediaryAgent')}
            header="Intermediary Agent"
          >
            <CommonStep
              onSubmit={(data: any) => onStepSubmit(data, 'intermediary_agent')}
              onCancel={onCancel}
              fields={fieldsDefinition?.['intermediary_agent']}
              responseField="intermediary_agent"
              defaultValues={stepsData.intermediaryAgent}
              fieldToDisplayableNameSchema="RECEIVER_FIELD_NAMES"
            />
          </StepItem>
        )}

        {isVisible('intermediaryAgentAccount') && (
          <StepItem
            key="intermediaryAgentAccount"
            {...getStep('intermediaryAgentAccount')}
            header="Intermediary Agent Account"
          >
            <CommonStep
              onSubmit={(data: any) =>
                onStepSubmit(data, 'intermediary_agent_account')
              }
              onCancel={onCancel}
              fields={fieldsDefinition?.['intermediary_agent_account']}
              responseField="intermediary_agent_account"
              defaultValues={stepsData.intermediaryAgentAccount}
              fieldToDisplayableNameSchema="RECEIVER_FIELD_NAMES"
            />
          </StepItem>
        )}

        <AccordionItem value="summary" className="rounded-2xl bg-white">
          <AccordionHeader>
            <AccordionTrigger className="group flex w-full cursor-pointer items-center justify-start gap-2 px-6 py-4 outline-offset-[-1px] group-last:radix-state-closed:rounded-b-lg">
              <StepStatusCircle
                step="summary"
                value={getStep('summary').index}
                isFinished={isFinished('summary')}
              />
              <h2
                data-testid={SUMMARY_STEP_BUTTON_TEST_ID}
                className="font-semibold"
              >
                Summary
              </h2>
              <ChevronDownIcon className="group-radix-state-open:scale-flip ml-auto w-7 transition-all duration-300 group-hover:!stroke-content-primary group-radix-state-closed:stroke-content-muted" />
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            {draftId && (
              <SummaryStep
                draftId={draftId}
                onSubmit={onSummarySubmit}
                onCancel={onCancel}
              />
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {uniqueReference && (
        <AcceptanceDialog reference={uniqueReference} onClose={onFinish} />
      )}
    </div>
  );
};
